import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Modal,
  message,
  Typography,
  Input,
  Form,
  Popconfirm,
  Table,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import PermissionService from '../services/permission';
import RoleService, { CreateRoleRequest, UpdateRoleRequest } from '../services/role';
import useRoles from '../hooks/useRoles';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useNavigate } from 'react-router-dom';
import {getServiceColumnsUnion} from '../config/order-columns-config';

const { Text } = Typography;

// 页面显示名称映射
const PAGE_NAMES: Record<string, string> = {
  'fee-management': '最新报价',
  'follow-up-management': '客户跟进',
  'trademark-monitor': '商标监控',
  'patent-payment-management': '专利年费监控',
  'status-translation': '状态翻译',
  'document-management': '文档管理',
  'order-management': '业务订单',
};

const RoleManagement: React.FC = () => {
  useDocumentTitle('角色权限 - 用户');
  const navigate = useNavigate();
  const { roles, loading: rolesLoading, refreshRoles } = useRoles();
  const [loading, setLoading] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);

  // 角色管理相关状态
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [roleForm] = Form.useForm();
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const { hasPermission } = useAuth();

  // 角色权限编辑已迁移到独立页面，此页不再加载权限分组与勾选状态

  // 同步页面权限到后台
  const syncPagePermissions = async () => {
    setLoading(true);
    try {
      const pagePermissions = Object.keys(PAGE_NAMES).map(pageKey => {
        // 根据页面类型定义不同的列权限
        // let columns: string[] = ['created_at', 'updated_at'];
        let columns: string[] = [];
        let actions: string[] = ['create', 'edit', 'delete', 'export', 'import'];
        let forms: { field_key: string; types: string[] }[] = [];

        switch (pageKey) {
          case 'fee-management':
            // 最新报价详情页与列表页面分离，需要单独同步查看详情权限。
            actions = [...actions, 'view_details'];
            columns = [...columns, 'service', 'sub_service', 'nation', 'service_type', 'category_type', 'submit_channel', 'contact_info',
                     'fee_official', 'fee_service', 'fee_inquiry', 'fee_notarization', 'fee_cost', 'fee_profit',
                     'fee_peer', 'dates_submit', 'dates_complete', 'documents', 'remark'];
            forms = [
              { field_key: 'service', types: ['view', 'edit'] },
              { field_key: 'nation', types: ['view', 'edit'] },
              { field_key: 'service_type', types: ['view', 'edit'] },
              { field_key: 'category_type', types: ['view', 'edit'] },
              { field_key: 'submit_channel', types: ['view', 'edit'] },
              { field_key: 'contact_info', types: ['view', 'edit'] },
              { field_key: 'documents', types: ['view', 'edit'] },
              { field_key: 'fee_official', types: ['view', 'edit'] },
              { field_key: 'fee_service', types: ['view', 'edit'] },
              { field_key: 'fee_inquiry', types: ['view', 'edit'] },
              { field_key: 'fee_notarization', types: ['view', 'edit'] },
              { field_key: 'fee_cost', types: ['view', 'edit'] },
              { field_key: 'fee_peer', types: ['view', 'edit'] },
              { field_key: 'dates_submit', types: ['view', 'edit'] },
              { field_key: 'dates_complete', types: ['view', 'edit'] },
              { field_key: 'remark', types: ['view', 'edit'] }
            ];
            break;
          case 'follow-up-management':
            // 客户跟进详情页与列表页面分离，需要单独同步查看详情权限。
            actions = [...actions, 'view_details'];
            columns = [...columns, 'contact_name', 'contact_info','service','sub_service',
                     'nation', 'client_type', 'source', 'intent', 'status', 'latest_record', 'created_at', 'updated_at', 'remark'];
            forms = [
              { field_key: 'sub_service', types: ['view', 'edit'] },
              { field_key: 'contact_name', types: ['view', 'edit'] },
              { field_key: 'contact_info', types: ['view', 'edit'] },
              { field_key: 'nation', types: ['view', 'edit'] },
              { field_key: 'service', types: ['view', 'edit'] },
              { field_key: 'client_type', types: ['view', 'edit'] },
              { field_key: 'source', types: ['view', 'edit'] },
              { field_key: 'intent', types: ['view', 'edit'] },
              { field_key: 'status', types: ['view', 'edit'] },
              { field_key: 'latest_record', types: ['view', 'edit'] },
              { field_key: 'created_at', types: ['view', 'edit'] },
              { field_key: 'remark', types: ['view', 'edit'] }
            ];
            break;
          case 'order-management':
            // 列显示权限：所有可能的表单字段都可能作为列表列展示
            actions = [...actions, 'view_details', 'update_status', 'copy', 'monitor'];

            const allColumns = getServiceColumnsUnion('all');

            const orderFormFields = ['id', ...allColumns];

            // columns 覆盖所有表单字段（所有字段都可能展示为列）
            columns = Array.from(new Set([...columns, ...orderFormFields]));
            // 表单权限
            forms = orderFormFields.map((key) => ({ field_key: key, types: ['view','edit'] }));
            break;
          case 'trademark-monitor':
            // 商标监控的列权限需要和 TrademarkMonitor 页面可配置列保持一致，避免角色权限里漏掉单独可控的字段。
            columns = [...columns, 'trademark_name', 'application_no', 'registration_no', 'status', 'nation',
                     'owner_name', 'owner_username', 'primary_class', 'sub_class', 'image',
                     'tags', 'application_at',   'attorney_name', 'attorney_email', 'correspondent_name_address', 'correspondent_email', 'remote_status', 'status_updated_at', 'sync_at', 'due_at', 'next_update_at', 'approve_at', 'created_at'];
            actions = [...actions, 'update_status'];
            forms = [
              { field_key: 'application_no', types: ['view', 'edit'] },
              { field_key: 'nation', types: ['view', 'edit'] },
              { field_key: 'user_email', types: ['view', 'edit'] }
            ];
            break;
          case 'patent-payment-management':
            columns = [...columns, 'order_id', 'order_no', 'year', 'started_at', 'due_at', 'grace_at', 'payment_at', 'status', 'remark', 'user_id', 'owner_username', 'created_at'];
            forms = [
              { field_key: 'order_id', types: ['view', 'edit'] },
              { field_key: 'order_no', types: ['view', 'edit'] },
              { field_key: 'year', types: ['view', 'edit'] },
              { field_key: 'started_at', types: ['view', 'edit'] },
              { field_key: 'due_at', types: ['view', 'edit'] },
              { field_key: 'grace_at', types: ['view', 'edit'] },
              { field_key: 'payment_at', types: ['view', 'edit'] },
              { field_key: 'status', types: ['view', 'edit'] },
              { field_key: 'remark', types: ['view', 'edit'] },
              { field_key: 'files', types: ['view', 'edit'] },
              { field_key: 'user_id', types: ['view', 'edit'] },
            ];
            break;
          case 'status-translation':
            columns = [...columns, 'original_status', 'translated_status', 'status_type', 'description'];
            actions = ['create', 'edit', 'delete', 'export', 'import'];
            forms = [
              { field_key: 'english', types: ['view', 'edit'] },
              { field_key: 'chinese', types: ['view', 'edit'] },
              { field_key: 'original_status', types: ['view', 'edit'] },
              { field_key: 'translated_status', types: ['view', 'edit'] },
              { field_key: 'status_type', types: ['view', 'edit'] },
              { field_key: 'description', types: ['view', 'edit'] }
            ];
            break;
          case 'document-management':
            // 文档详情页与列表页面分离，需要单独同步查看详情权限。
            actions = [...actions, 'view_details'];
            columns = [...columns, 'name', 'file_type', 'file_size', 'service_type', 'nation', 'media_files', 'remark', 'upload_at', 'order_no', 'owner_name'];
            actions = [...actions, 'upload'];
            forms = [
              { field_key: 'name', types: ['view', 'edit'] },
              { field_key: 'order_no', types: ['view', 'edit'] },
              { field_key: 'service_type', types: ['view', 'edit'] },
              { field_key: 'nation', types: ['view', 'edit'] },
              { field_key: 'file_type', types: ['view', 'edit'] },
              { field_key: 'file_size', types: ['view', 'edit'] },
              { field_key: 'upload_at', types: ['view', 'edit'] },
              { field_key: 'owner_name', types: ['view', 'edit'] },
              { field_key: 'remark', types: ['view', 'edit'] },
              { field_key: 'files', types: ['view', 'edit'] }
            ];
            break;
          default:
            columns = [...columns, 'name', 'status'];
            forms = [
              { field_key: 'name', types: ['view', 'edit'] },
              { field_key: 'status', types: ['view', 'edit'] }
            ];
        }

        return {
          page_key: pageKey,
          columns,
          actions,
          forms
        };
      });

      const response = await PermissionService.syncPagePermissions(pagePermissions);
      if (response.success) {
        message.success(`权限同步成功: ${response.message}`);
      } else {
        message.error(getApiResponseErrorMessage(response, '同步失败'));
      }
    } catch (error) {
      message.error('同步权限失败');
      console.error('Sync permissions error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 显示同步确认对话框
  const handleSyncClick = () => {
    setShowSyncConfirm(true);
  };

  // 确认同步
  const handleSyncConfirm = () => {
    setShowSyncConfirm(false);
    syncPagePermissions();
  };

  // 角色管理相关函数
  const handleCreateRole = () => {
    setEditingRole(null);
    roleForm.resetFields();
    setRoleModalVisible(true);
  };

  const handleEditRole = (role: any) => {
    setEditingRole(role);
    roleForm.setFieldsValue({
      name: role.name,
      display_name: role.display_name,
    });
    setRoleModalVisible(true);
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      const response = await RoleService.deleteRole(roleId);
      if (response.success) {
        message.success('角色删除成功');
        refreshRoles();
      } else {
        message.error(getApiResponseErrorMessage(response, '删除失败'));
      }
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '删除失败'));
    }
  };

  const handleRoleSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (editingRole) {
        // Update role
        const updateData: UpdateRoleRequest = {
          name: values.name,
          display_name: values.display_name,
        };

        const response = await RoleService.updateRole(editingRole.id, updateData);
        if (response.success) {
          message.success('角色更新成功');
        } else {
          message.error(getApiResponseErrorMessage(response, '更新失败'));
          return;
        }
      } else {
        // Create role
        const createData: CreateRoleRequest = {
          name: values.name,
          display_name: values.display_name,
        };

        const response = await RoleService.createRole(createData);
        if (response.success) {
          message.success('角色添加成功');
        } else {
          message.error(getApiResponseErrorMessage(response, '创建失败'));
          return;
        }
      }

      setRoleModalVisible(false);
      refreshRoles();
    } catch (error: any) {
      message.error(getAxiosErrorMessage(error, '操作失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshRoles();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="角色管理"
        extra={
          <Space>
            {hasPermission('role.view') && (
            <Button
              onClick={async () => {
                try {
                  const { blob, filename } = await RoleService.exportRoles();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = filename || 'roles_export.json';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  message.error('导出失败');
                }
              }}
              icon={<DownloadOutlined />}
            >
              导出角色权限
            </Button>)}
            {hasPermission('role.update') && (
            <Button
              onClick={() => fileInputRef.current?.click()}
              icon={<UploadOutlined />}
              loading={importing}
            >
              导入角色权限
            </Button>)}
            <input
              type="file"
              accept="application/json,.json"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setImporting(true);
                try {
                  const resp = await RoleService.importRoles(file);
                  if (resp.success) {
                    message.success('导入成功');
                    await refreshRoles();
                  } else {
                    message.error(getApiResponseErrorMessage(resp, '导入失败'));
                  }
                } catch (err) {
                  message.error('导入失败');
                } finally {
                  setImporting(false);
                  if (e.target) e.target.value = '';
                }
              }}
            />
            <Button
              type="primary"
              onClick={handleCreateRole}
              icon={<PlusOutlined />}
              data-testid="btn-create-role"
            >
              新建角色
            </Button>
            <Button
              type="default"
              onClick={handleSyncClick}
              loading={loading}
              icon={<ReloadOutlined />}
              data-testid="btn-sync-permissions"
            >
              同步页面权限
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={rolesLoading || loading}
          dataSource={roles}
          pagination={false}
          columns={[
            {
              title: '显示名称',
              dataIndex: 'display_name',
              render: (_: any, record: any) => record.display_name || record.name,
            },
            {
              title: '标识',
              dataIndex: 'name',
            },
            {
              title: '类型',
              render: (_: any, record: any) => (record.name === 'admin' ? <Text type="secondary">系统角色</Text> : '自定义'),
            },
            {
              title: '操作',
              render: (_: any, record: any) => {
                const isSystemRole = ['admin'].includes(record.name);
                return (
                  <Space size="small">
                    <Button type="link" onClick={() => navigate(`/roles/${record.id}`)}>
                      编辑权限
                    </Button>
                    {!isSystemRole && (
                      <>
                        <Button type="link" onClick={() => handleEditRole(record)}>
                          编辑
                        </Button>
                        <Popconfirm
                          title="确定要删除这个角色吗？"
                          onConfirm={() => handleDeleteRole(record.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button type="link" danger>
                            删除
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                    {isSystemRole && (
                      <Text type="secondary">管理员自动拥有所有权限</Text>
                    )}
                  </Space>
                );
              }
            }
          ]}
        />
      </Card>

      {/* 角色管理 Modal */}
      <Modal
        title={editingRole ? '编辑角色' : '新建角色'}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        footer={null}
        width={500}
        data-testid="modal-role"
      >
        <Form
          form={roleForm}
          layout="vertical"
          onFinish={handleRoleSubmit}
        >
          <Form.Item
            name="name"
            label="角色标识"
            rules={[
              { required: true, message: '请输入角色标识!' },
              { min: 2, message: '角色标识至少2个字符!' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: '角色标识只能包含字母、数字、下划线和横线!' }
            ]}
          >
            <Input placeholder="例如: editor, customer_service" data-testid="input-role-name" />
          </Form.Item>

          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[
              { min: 2, message: '显示名称至少2个字符!' }
            ]}
          >
            <Input placeholder="请输入用户友好的显示名称，例如：编辑员、客服" data-testid="input-role-display-name" />
          </Form.Item>

          <div style={{
            backgroundColor: '#f6f8fa',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            color: '#666'
          }}>
            <strong>提示：</strong>创建角色后，可在列表中点击“编辑权限”进入权限编辑页面
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setRoleModalVisible(false)} data-testid="btn-role-cancel">
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} data-testid="btn-role-submit">
                {editingRole ? '更新' : '添加'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 同步确认对话框 */}
      <Modal
        title="确认同步"
        open={showSyncConfirm}
        onOk={handleSyncConfirm}
        onCancel={() => setShowSyncConfirm(false)}
        okText="确认同步"
        cancelText="取消"
        confirmLoading={loading}
        data-testid="modal-sync-confirm"
        okButtonProps={{ 'data-testid': 'btn-sync-confirm' } as any}
        cancelButtonProps={{ 'data-testid': 'btn-sync-cancel' } as any}
      >
        <p>确认要同步页面权限到后台吗？</p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          这将会根据当前系统页面结构自动生成和更新权限设置，可能会影响现有的权限配置。
        </p>
      </Modal>
    </div>
  );
};

export default RoleManagement;
